// frontend/src/pages/EmailTriagePage.js
// v1.0 - Email Inbox: AI-reviewed client emails awaiting human approval.

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useBranding } from '../context/BrandingContext';
import FitenkoPochta from './FitenkoPochta';
import './LeadsPage.css';
import './EmailTriagePage.css';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return dateString; }
};

// --- Single Email Card ---
const EmailCard = ({ email, onActionDone }) => {
    const extracted = email.ai_extracted || {};
    const [reply, setReply] = useState(email.ai_draft_reply || '');
    const [working, setWorking] = useState(false);

    const handleApprove = async () => {
        setWorking(true);
        try {
            await apiService.approveEmail(email.id, reply);
            toast.success('Reply approved and sent.');
            onActionDone(email.id);
        } catch (err) {
            toast.error(`Failed to send reply: ${err.message}`);
        } finally {
            setWorking(false);
        }
    };

    const handleDismiss = async () => {
        setWorking(true);
        try {
            await apiService.dismissEmail(email.id);
            toast.success('Email dismissed.');
            onActionDone(email.id);
        } catch (err) {
            toast.error(`Failed to dismiss: ${err.message}`);
        } finally {
            setWorking(false);
        }
    };

    const chips = [
        extracted.visa_type && { label: 'Visa', value: extracted.visa_type },
        extracted.urgency && { label: 'Urgency', value: extracted.urgency },
        extracted.language && { label: 'Language', value: extracted.language },
        extracted.client_phone && { label: 'Phone', value: extracted.client_phone },
    ].filter(Boolean);

    return (
        <div className="card email-card">
            <div className="email-card-header">
                <div className="email-from">
                    <span className="email-from-name">{email.from_name || email.from_email}</span>
                    {email.from_name && <span className="email-from-addr">&lt;{email.from_email}&gt;</span>}
                </div>
                <span className="email-date">{formatDate(email.received_at)}</span>
            </div>

            <h3 className="email-subject">{email.subject || '(no subject)'}</h3>

            {email.ai_summary && (
                <div className="email-summary">
                    <span className="email-summary-label">AI Summary</span>
                    <p>{email.ai_summary}</p>
                </div>
            )}

            {chips.length > 0 && (
                <div className="email-chips">
                    {chips.map((c) => (
                        <span key={c.label} className="email-chip">
                            <strong>{c.label}:</strong> {c.value}
                        </span>
                    ))}
                </div>
            )}

            <label className="email-reply-label">
                <span>Proposed reply (editable)</span>
                <textarea
                    className="form-input email-reply-textarea"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={6}
                    placeholder="Write or edit the reply before sending..."
                />
            </label>

            <div className="email-card-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleDismiss} disabled={working}>
                    {working ? 'Working...' : 'Dismiss'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleApprove} disabled={working}>
                    {working ? 'Sending...' : 'Approve & Send'}
                </button>
            </div>
        </div>
    );
};

// --- Main Page ---
const EmailTriagePage = () => {
    const { isFitenko } = useBranding();
    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const data = await apiService.getPendingEmails();
                setEmails(Array.isArray(data) ? data : []);
            } catch (error) {
                toast.error(`Failed to load emails: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmails();
    }, []);

    // 🔴 РАННИЙ ВОЗВРАТ СТОИТ ПОСЛЕ ПОСЛЕДНЕГО ХУКА — прецедент Dashboard.js:99
    //    и LeadsPage.js:248. Раньше ставить нельзя: порядок хуков React обязан
    //    быть один и тот же на каждой отрисовке, а признак арендатора приходит
    //    асинхронно и меняется. Ниже хуков нет — только вычисления и разметка.
    //    Экран арендатора fitenko живёт СВОИМ файлом: это сразу и рубеж, и
    //    граница измерения цены рисунка. Путь mlg, eln и elfny ниже НЕ ТРОНУТ.
    if (isFitenko) return <FitenkoPochta />;

    const handleActionDone = (emailId) => {
        setEmails(prev => prev.filter(e => e.id !== emailId));
    };

    if (isLoading) return <LoadingSpinner message="Loading emails..." />;

    return (
        <div className="leads-page-container container">
            <header className="page-header">
                <h1>Email Inbox</h1>
                <p>AI-reviewed client emails awaiting your approval.</p>
            </header>

            {emails.length > 0 ? (
                <div className="email-card-list">
                    {emails.map(email => (
                        <EmailCard key={email.id} email={email} onActionDone={handleActionDone} />
                    ))}
                </div>
            ) : (
                <div className="card results-card">
                    <p className="no-results">No emails awaiting review.</p>
                </div>
            )}
        </div>
    );
};

export default EmailTriagePage;
